<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Role Management</h3>
      <el-button type="primary" size="small" @click="openCreate">New Role</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Role Name">
        <el-input v-model="query.roleName" clearable placeholder="Please enter a role name" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="roleId" label="ID" width="80" />
      <el-table-column prop="roleName" label="Role Name" min-width="160" />
      <el-table-column prop="roleKey" label="Permission Key" min-width="160" />
      <el-table-column prop="remark" label="Remark" min-width="180" show-overflow-tooltip />
      <el-table-column prop="status" label="Status" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === '0' ? 'success' : 'danger'">
            {{ scope.row.status === '0' ? 'Active' : 'Disabled' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="Created At" min-width="160" />
      <el-table-column label="Actions" width="230" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" @click="openPermission(scope.row)">Permissions</el-button>
          <el-button
            type="text"
            size="small"
            class="danger"
            :disabled="scope.row.roleKey === 'admin'"
            @click="remove(scope.row)"
          >Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px">
      <el-form :model="form" label-width="90px" size="small">
        <el-form-item label="Role Name">
          <el-input v-model="form.roleName" placeholder="Example: Operations Admin" />
        </el-form-item>
        <el-form-item label="Permission Key">
          <el-input v-model="form.roleKey" placeholder="Example: operator" :disabled="form.roleKey === 'admin'" />
        </el-form-item>
        <el-form-item label="Status">
          <el-radio-group v-model="form.status">
            <el-radio label="0">Active</el-radio>
            <el-radio label="1">Disabled</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Remark">
          <el-input v-model="form.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="permissionTitle" :visible.sync="permissionVisible" width="560px">
      <div class="tree-toolbar">
        <el-button size="mini" @click="checkAll">Select All</el-button>
        <el-button size="mini" @click="clearAll">Clear</el-button>
      </div>
      <el-tree
        ref="menuTree"
        :data="menuTree"
        :props="{ label: 'label', children: 'children' }"
        node-key="id"
        show-checkbox
        default-expand-all
      />
      <span slot="footer">
        <el-button @click="permissionVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submitPermission">Save Permissions</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { roleApi, menuApi } from '@/api/system'

const emptyForm = () => ({
  roleId: null,
  roleName: '',
  roleKey: '',
  status: '0',
  remark: ''
})

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, roleName: '' })

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: 'New Role',
      form: emptyForm(),
      permissionVisible: false,
      permissionTitle: 'Menu Permissions',
      currentRole: null,
      menuTree: []
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await roleApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = 'New Role'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Role'
      this.form = { ...emptyForm(), ...row }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.roleName || !this.form.roleKey) {
        this.$message.warning('Please enter the role name and permission key')
        return
      }
      if (this.form.roleId) await roleApi.update(this.form)
      else await roleApi.create(this.form)
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    async remove(row) {
      await this.$confirm(`Delete role ${row.roleName}?`, 'Confirm', { type: 'warning' })
      await roleApi.remove(row.roleId)
      this.$message.success('Deleted successfully')
      this.loadData()
    },
    async openPermission(row) {
      this.currentRole = row
      this.permissionTitle = `Menu Permissions - ${row.roleName}`
      this.permissionVisible = true
      const treeRes = await menuApi.tree()
      const idsRes = await roleApi.menuIds(row.roleId)
      this.menuTree = treeRes.data || []
      this.$nextTick(() => {
        this.$refs.menuTree.setCheckedKeys(this.checkedLeafIds(this.menuTree, idsRes.data || []))
      })
    },
    async submitPermission() {
      const tree = this.$refs.menuTree
      const menuIds = [...tree.getCheckedKeys(), ...tree.getHalfCheckedKeys()]
      await roleApi.updateMenus(this.currentRole.roleId, menuIds)
      this.$message.success('Permissions saved. Changes take effect after affected users refresh.')
      this.permissionVisible = false
    },
    checkAll() {
      const ids = []
      const walk = nodes => {
        nodes.forEach(node => {
          ids.push(node.id)
          if (node.children && node.children.length) walk(node.children)
        })
      }
      walk(this.menuTree)
      this.$refs.menuTree.setCheckedKeys(ids)
    },
    clearAll() {
      this.$refs.menuTree.setCheckedKeys([])
    },
    checkedLeafIds(nodes, selectedIds) {
      const selected = new Set(selectedIds)
      const ids = []
      const walk = list => {
        list.forEach(node => {
          const children = node.children || []
          if (children.length) walk(children)
          else if (selected.has(node.id)) ids.push(node.id)
        })
      }
      walk(nodes)
      return ids
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
.tree-toolbar { margin-bottom: 12px; }
</style>
